import * as R from 'ramda';
import * as P from 'plow-js';
import styled from 'styled-components';
import { Waypoint } from 'react-waypoint';
import React, { useRef, useEffect } from 'react';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// components
import { TextComponent } from '../../text';
// hocs
import { LocalLoader } from '../../local-loader';
import { withBooleanStatus } from '../../../hocs';
// icons
import * as I from '../../../svgs';
// ui
import {
  Box,
  Flex,
  IconWrapper,
  RelativeFlex,
  pulseOpacity,
  StickedWrapper,
} from '../../../ui';
// utilities
import routesMap from '../../../utilities/routes';
// feature table
import TitleSort from './title-sort';
import TitleFilter from './title-filter';
import {
  ActionCell,
  TotalField,
  EnumElement,
  StateElement,
  EditableCell,
  BranchElement,
  CurrencyField,
  ClickableLink,
  BooleanElement,
  UomRelatedField,
  LocationComponent,
  FreightClassElement,
  SelectDropdownMuiCell,
  ActionCellWithExpandedContainer,
} from './elements';
import {
  TableRow,
  TitleRow,
  TableCell,
  TitleCell,
  TableInner,
  WithBoxShadow,
  FlexContainer,
  EmptyTableList,
  CellWithBackground,
  DisableRelativeFlex,
  TitleCellWithBackground,
} from '../ui';
import {
  setName,
  getWidth,
  getRowBgColor,
  getRelativeWidth,
  isColumnResizable,
  getIsSearchableField,
  filterTypesWithMuiTitleInput,
} from './helpers';
import {
  CustomElement,
  ExpandedDetails,
  CheckBoxComponent,
  StickedCellWithIcons,
  RightStickedComponent,
  AdditionalRowComponent,
} from './table-components';
//////////////////////////////////////////////////

const TITLE_FILTER_RESERVED_WIDTH = 46;

const TitleCellComponent = ({
  name,
  type,
  width,
  sortable,
  minWidth,
  maxWidth,
  resizable,
  reference,
  fieldName,
  searchable,
  fieldProps,
  resizeByGuid,
  tableSettings,
  resizeObserver,
  titleSortValue,
  clickableTitle,
  titleFilterValue,
  shouldUseUIFilters,
  customTitleComponent,
  handleTableTitleFilter,
  useNewTitleFilterInputs,
}) => {
  const { searchableTitles, disableRefFilter, handleTitleCellClick } = tableSettings;

  const cellRef = useRef(null);

  const storedWidth = R.prop(fieldName, resizeByGuid);

  useEffect(() => {
    if (R.and(resizable, G.isNotNil(resizeObserver))) {
      const cellEl = cellRef.current;

      if (cellEl) {
        cellEl[GC.FIELD_NAME] = fieldName;
        resizeObserver?.observe(cellEl);
      }
    }
  }, [fieldName, resizeObserver]);

  const clickHandler = G.ifElse(
    R.and(clickableTitle, G.isFunction(handleTitleCellClick)),
    () => handleTitleCellClick(fieldName),
    () => {},
  );

  const resultWidth = R.or(storedWidth, width);
  const cellMinWidth = R.or(minWidth, R.divide(width, 2));
  const needCalcWidth = R.or(R.isNotNil(R.prop(GC.FIELD_OPTIONS, fieldProps)), R.prop('needCalcWidth', fieldProps));
  const isSearchableField = getIsSearchableField({ reference, searchable, disableRefFilter });
  const relativeWidth = getRelativeWidth({ type, resizable, needCalcWidth });
  const disableFilter = G.isTrue(R.prop('disableFilter', fieldProps));

  if (R.and(isSearchableField, searchableTitles)) {
    return (
      <Flex
        zIndex={0}
        p='4px 8px'
        height='100%'
        ref={cellRef}
        width={resultWidth}
        flexDirection='column'
        onClick={clickHandler}
        alignItems='flex-start'
        minWidth={cellMinWidth}
        style={{width: resultWidth}} //<-- necessary to reset resize
        maxWidth={R.multiply(width, 2)}
        overflow={G.ifElse(resizable, 'auto', 'inherit')}
        resize={G.ifElse(resizable, 'horizontal', 'none')}
        justifyContent={G.ifElse(useNewTitleFilterInputs, 'flex-end', 'normal')}
      >
        {
          G.isFunction(customTitleComponent) &&
          customTitleComponent(fieldName)
        }
        {
          R.or(
            R.and(
              G.isNilOrEmpty(useNewTitleFilterInputs),
              G.isNilOrEmpty(customTitleComponent),
            ),
            R.and(
              G.isNotNilAndNotEmpty(useNewTitleFilterInputs),
              G.notContain(fieldProps.type, filterTypesWithMuiTitleInput),
            ),
          ) &&
          <Flex
            maxWidth='100%'
            overflow='hidden'
            alignItems='center'
            height='-webkit-fill-available'
          >
            <TextComponent title={name} fontSize={11} withEllipsis={true} color={G.getTheme('colors.darkGrey')}>
              {name}
            </TextComponent>
          </Flex>
        }
        {
          G.isNotNilAndNotEmpty(fieldProps) &&
          <DisableRelativeFlex
            width={relativeWidth}
            disabled={disableFilter}
            height='-webkit-fill-available'
          >
            <TitleFilter
              title={name}
              width='100%'
              fieldProps={fieldProps}
              titleFilterValue={titleFilterValue}
              fontSize={tableSettings.titleFontSize}
              shouldUseUIFilters={shouldUseUIFilters}
              handleTableTitleFilter={handleTableTitleFilter}
              useNewTitleFilterInputs={useNewTitleFilterInputs}
              maxWidth={R.subtract(resultWidth, TITLE_FILTER_RESERVED_WIDTH)}
            />
            {
              sortable &&
              <TitleSort
                fieldProps={fieldProps}
                titleSortValue={titleSortValue}
                handleTableTitleFilter={handleTableTitleFilter}
              />
            }
          </DisableRelativeFlex>
        }
      </Flex>
    );
  }

  return (
    <Flex
      p='8px'
      height='100%'
      ref={cellRef}
      width={resultWidth}
      onClick={clickHandler}
      minWidth={cellMinWidth}
      style={{width: resultWidth}} //<-- necessary to reset resize
      maxWidth={R.multiply(width, 2)}
      overflow={G.ifElse(resizable, 'auto', 'inherit')}
      resize={G.ifElse(resizable, 'horizontal', 'none')}
    >
      {
        G.isFunction(customTitleComponent) &&
        customTitleComponent(fieldName)
      }
      {
        G.isNilOrEmpty(customTitleComponent) &&
        <RelativeFlex
          height='100%'
          maxWidth='100%'
          alignItems='center'
          width={relativeWidth}
        >
          <TextComponent title={name} withEllipsis={true} color={G.getTheme('colors.darkGrey')}>
            {name}
          </TextComponent>
          {
            G.isAllTrue(sortable, R.not(reference), G.isNotNilAndNotEmpty(fieldProps)) &&
            <TitleSort
              fieldProps={fieldProps}
              titleSortValue={titleSortValue}
              handleTableTitleFilter={handleTableTitleFilter}
            />
          }
        </RelativeFlex>
      }
    </Flex>
  );
};

const renderTitleFields = (props) => {
  const {
    fields,
    filterProps,
    resizeByGuid,
    showEditView,
    tableSettings,
    columnSettings,
    resizeObserver,
    titleSortValues,
    tableResizeData,
    tableTitleFilters,
    shouldUseUIFilters,
    useSearchableColumns,
    withResizableColumns,
    handleTableTitleFilter,
    useNewTitleFilterInputs,
    handleUpdateTableResize,
    setResizedFieldToLocalStorage,
  } = props;

  const {
    coloredHeader,
    useMainColors,
    moreBtnCellWidth,
    withoutFieldSort,
    additionalTitleBgColor,
  } = tableSettings;

  let left = R.path(['checkBoxCellWidth'], tableSettings);

  const fieldsLength = R.prop('length', fields);

  return fields.map((field, i) => {
    const { right, freezed, reference, freezedRight, clickableTitle, customTitleComponent } = field;

    const fieldName = G.getPropFromObject(GC.FIELD_NAME, field);

    const name = setName(field, props);
    const type = R.path([fieldName, 'type'], columnSettings);
    const isCreatable = R.includes(type, ['state']);

    let fieldProps = R.pathOr({}, [fieldName], filterProps);

    if (G.isNotNilAndNotEmpty(fieldProps)) {
      fieldProps = R.assoc('isCreatable', isCreatable, fieldProps)
    }

    const isDynamicField = G.isDynamicField(fieldName);

    if (isDynamicField) {
      fieldProps = {
        ...fieldProps,
        ...G.getFieldTypeAndSearchable(fieldName),
        value: fieldName,
      };
    }

    if (reference) {
      fieldProps = {
        value: fieldName,
        type: 'reference',
      };
    }

    const resizable = isColumnResizable({
      fieldName,
      fieldProps,
      columnSettings,
      withResizableColumns,
    });

    const sortable = isDynamicField ?
      false :
      R.pathOr(R.not(withoutFieldSort), [fieldName, 'sortable'], columnSettings);

    const searchable = R.or(
      R.prop('searchable', fieldProps),
      R.pathOr(G.isTrue(useSearchableColumns), [fieldName, 'searchable'], columnSettings),
    );

    const width = getWidth({ fieldName, filterProps, columnSettings, useNewTitleFilterInputs });

    const minWidth = R.path([fieldName, 'styles', 'titleInputMinWidth'], columnSettings);
    const maxWidth = R.path([fieldName, 'styles', 'titleInputMaxWidth'], columnSettings);

    const titleFilterValue = R.pathOr({}, [fieldName], tableTitleFilters);
    const titleSortValue = R.pathOr({}, [fieldName], titleSortValues);

    let storedWidth;

    if (resizable) {
      storedWidth = R.prop(fieldName, resizeByGuid);
    }

    const newWidth = R.or(storedWidth, width);

    const cellLeft = left;

    left = R.add(left, R.or(newWidth, 0));

    if (R.or(freezed, freezedRight)) {
      const positionOption = G.ifElse(
        freezed,
        { left: cellLeft },
        { right: R.or(right, R.or(moreBtnCellWidth, '0px')) },
      );

      const zIndex = R.subtract(fieldsLength, i);

      return (
        <StickedWrapper
          bgColor='unset'
          zIndex={zIndex}
          width={newWidth}
          {...positionOption}
          key={`sticked-field-table-${i}`}
        >
          <TitleCellWithBackground
            colored={coloredHeader}
            mainColor={useMainColors}
            bgColor={additionalTitleBgColor}
            className='cell-with-background'
          >
            {
              R.not(R.path([field.name, 'withoutTitle'], columnSettings)) &&
              <TitleCellComponent
                name={name}
                type={type}
                width={width}
                minWidth={minWidth}
                sortable={sortable}
                maxWidth={maxWidth}
                resizable={resizable}
                reference={reference}
                fieldName={fieldName}
                searchable={searchable}
                fieldProps={fieldProps}
                key={`field-table-${i}`}
                resizeByGuid={resizeByGuid}
                tableSettings={tableSettings}
                resizeObserver={resizeObserver}
                titleSortValue={titleSortValue}
                clickableTitle={clickableTitle}
                tableResizeData={tableResizeData}
                titleFilterValue={titleFilterValue}
                customTitleComponent={customTitleComponent}
                handleTableTitleFilter={handleTableTitleFilter}
                useNewTitleFilterInputs={useNewTitleFilterInputs}
                handleUpdateTableResize={handleUpdateTableResize}
                setResizedFieldToLocalStorage={setResizedFieldToLocalStorage}
              />
            }
          </TitleCellWithBackground>
        </StickedWrapper>
      );
    }

    return (
      <TitleCellComponent
        name={name}
        type={type}
        width={width}
        sortable={sortable}
        minWidth={minWidth}
        maxWidth={maxWidth}
        resizable={resizable}
        reference={reference}
        fieldName={fieldName}
        searchable={searchable}
        fieldProps={fieldProps}
        key={`field-table-${i}`}
        resizeByGuid={resizeByGuid}
        showEditView={showEditView}
        tableSettings={tableSettings}
        resizeObserver={resizeObserver}
        titleSortValue={titleSortValue}
        clickableTitle={clickableTitle}
        tableResizeData={tableResizeData}
        titleFilterValue={titleFilterValue}
        shouldUseUIFilters={shouldUseUIFilters}
        customTitleComponent={customTitleComponent}
        handleTableTitleFilter={handleTableTitleFilter}
        useNewTitleFilterInputs={useNewTitleFilterInputs}
        handleUpdateTableResize={handleUpdateTableResize}
        setResizedFieldToLocalStorage={setResizedFieldToLocalStorage}
      />
    );
  });
};

export const TitleRowComponent = (props) => {
  const {
    report,
    itemList,
    allChecked,
    filterProps,
    resizeByGuid,
    showEditView,
    onOptionClick,
    tableSettings,
    columnSettings,
    resizeObserver,
    withoutTitleRow,
    titleSortValues,
    tableResizeData,
    showTitleActions,
    lastFreezedIndex,
    tableTitleFilters,
    titleActionsLabel,
    shouldUseUIFilters,
    useSearchableColumns,
    withResizableColumns,
    handleClickResetIcon,
    handleTableTitleFilter,
    handleClickTitleActions,
    useNewTitleFilterInputs,
    handleUpdateTableResize,
    setResizedFieldToLocalStorage,
  } = props;

  if (G.isTrue(withoutTitleRow)) return null;

  const disabled = R.any((item) => {
    const editable = R.prop('editable', item);

    return G.ifElse(G.isBoolean(editable), R.not(editable), false);
  }, R.or(itemList, []));

  const shouldShowTitleActions = R.or(
    G.isTrue(withResizableColumns),
    G.isTrue(showTitleActions),
  );
  const titleActionsClickHandler = R.or(handleClickTitleActions, handleClickResetIcon);
  const titleActionsTooltip = R.or(titleActionsLabel, 'Reset table columns width');

  return (
    <StickedWrapper
      top='0px'
      zIndex={tableSettings.titleRowZIndex}
    >
      <TitleRow
        color={tableSettings.titleTextColor}
        height={tableSettings.titleRowHeight}
        colored={tableSettings.coloredHeader}
        fontSize={tableSettings.titleFontSize}
        mainColor={tableSettings.useMainColors}
        bgColor={tableSettings.additionalTitleBgColor}
      >
        <StickedWrapper
          left='0px'
          zIndex={1}
          bgColor='unset'
          width={tableSettings.checkBoxCellWidth}
          minWidth={tableSettings.checkBoxCellWidth}
        >
          <TitleCellWithBackground
            className='cell-with-background'
            colored={tableSettings.coloredHeader}
            mainColor={tableSettings.useMainColors}
            bgColor={tableSettings.additionalTitleBgColor}
          >
            {
              R.and(
                G.isTrue(tableSettings.allowSelectAll),
                G.isTrue(tableSettings.allowSelectItems),
              ) &&
              <CheckBoxComponent
                id='all'
                p='0 10px 0 6px'
                name='select all'
                disabled={disabled}
                checked={Boolean(allChecked)}
                ml={G.ifElse(tableSettings.expandableItems, 27, 'unset')}
                justifyContent={tableSettings.checkBoxCellJustifyContent}
                checkBoxCellBorderColor={tableSettings.checkBoxCellBorderColor}
                onOptionClick={
                  G.ifElse(
                    R.or(
                      disabled,
                      R.not(R.path(['length'], itemList))
                    ),
                    () => {},
                    onOptionClick,
                  )
                }
                mainColors={tableSettings.useMainColors}
              />
            }
          </TitleCellWithBackground>
        </StickedWrapper>
        {
          renderTitleFields({
            filterProps,
            resizeByGuid,
            showEditView,
            tableSettings,
            columnSettings,
            resizeObserver,
            titleSortValues,
            tableResizeData,
            lastFreezedIndex,
            tableTitleFilters,
            shouldUseUIFilters,
            useSearchableColumns,
            withResizableColumns,
            handleTableTitleFilter,
            useNewTitleFilterInputs,
            handleUpdateTableResize,
            setResizedFieldToLocalStorage,
            reportType: R.prop(GC.FIELD_TYPE, report),
            fields: R.sortBy(R.prop('sequence'), report.fields),
          })
        }
        {
          G.isTrue(tableSettings.allowEditBtn) &&
          <StickedWrapper
            ml='auto'
            right='0px'
            bgColor='unset'
            width={tableSettings.moreBtnCellWidth}
            minWidth={tableSettings.moreBtnCellWidth}
          >
            <TitleCellWithBackground
              className='cell-with-background'
              colored={tableSettings.coloredHeader}
              mainColor={tableSettings.useMainColors}
              bgColor={tableSettings.additionalTitleBgColor}
            >
              { shouldShowTitleActions &&
                <FlexContainer
                  p='8px'
                  height='100%'
                  justify='center'
                  direction='column'
                  title={titleActionsTooltip}
                  align={tableSettings.moreBtnTitleCellAlign}
                >
                  <IconWrapper px={12} cursor='pointer' onClick={titleActionsClickHandler}>
                    {I.threeDots()}
                  </IconWrapper>
                </FlexContainer>
              }
              { R.not(shouldShowTitleActions) &&
                <TitleCell p='8px' width={tableSettings.moreBtnCellWidth} minWidth={tableSettings.moreBtnCellWidth} />
              }
            </TitleCellWithBackground>
          </StickedWrapper>
        }
      </TitleRow>
    </StickedWrapper>
  );
};

const TableFieldComponent = (props) => (
  <TableCell
    key={props.key}
    width={props.width}
    minWidth={props.width}
    p={R.pathOr('8px', ['field', 'p'], props)}
  >
    <FlexContainer height='100%' justify='center' direction='column'>
      <TextComponent
        display='block'
        overflow='hidden'
        title={props.value}
        withEllipsis={true}
        maxWidth={`calc(${props.width} - 8px)`}
      >
        {props.value}
      </TextComponent>
    </FlexContainer>
  </TableCell>
);

const UomRelatedFields = [
  GC.FIELD_WEIGHT,
  GC.FIELD_DISTANCE,
  GC.FIELD_ITEM_VOLUME,
  GC.FIELD_TEMPERATURE,
];

const TableField = (props) => {
  const {
    data,
    field,
    width,
    uiType,
    settings,
    isCustom,
    rowIndex,
    resizable,
    fieldData,
    callbackData,
    resizeByGuid,
    cellPermissions,
    useExpandedContainer,
    openContainerAsNewWindow,
  } = props;

  let storedWidth;

  if (resizable) {
    const fieldName = R.prop(GC.FIELD_NAME, field);
    storedWidth = R.prop(fieldName, resizeByGuid);
  }

  const newWidth = R.or(storedWidth, width);

  if (isCustom) {
    return (
      <CustomElement
        data={data}
        field={field}
        width={newWidth}
        rowIndex={rowIndex}
        fieldData={fieldData}
        bgColor={settings.bgColor}
        callbackData={callbackData}
        component={settings.customComponent}
      />
    );
  }

  if (R.and(R.includes(uiType, UomRelatedFields), G.isNotNilAndNotEmpty(fieldData))) {
    return (
      <UomRelatedField
        type={uiType}
        field={field}
        width={newWidth}
        fieldData={fieldData}
        withoutConversion={R.pathOr(false, ['withoutConversion'], settings)}
      />
    );
  }

  if (R.equals(uiType, GC.FIELD_CURRENCY)) {
    return (
      <CurrencyField data={data} value={fieldData} field={field} width={newWidth} callbackData={callbackData} />
    );
  }

  if (R.equals(uiType, 'editableText')) {
    return (
      <EditableCell
        data={data}
        field={field}
        width={newWidth}
        value={fieldData}
        settings={settings}
        callbackData={callbackData}
      />
    );
  }

  if (R.equals(uiType, 'boolean')) {
    return (
      <BooleanElement value={fieldData} data={data} width={newWidth} field={field} />
    );
  }

  if (R.equals(uiType, 'booleanReverse')) {
    return (
      <BooleanElement value={R.not(fieldData)} data={data} width={newWidth} field={field} />
    );
  }

  if (R.equals(uiType, 'enum')) {
    return (
      <EnumElement value={R.or(fieldData, '')} data={data} width={newWidth} field={field} />
    );
  }

  if (R.equals(uiType, 'freightClass')) {
    return (
      <FreightClassElement value={fieldData} data={data} width={newWidth} field={field} />
    );
  }

  if (R.equals(uiType, 'enterpriseGuid')) {
    return (
      <BranchElement value={R.or(fieldData, '')} data={data} width={newWidth} field={field} />
    );
  }

  if (R.equals(uiType, 'clickableLink')) {
    return (
      <ClickableLink
        data={data}
        field={field}
        width={newWidth}
        settings={settings}
        value={R.or(fieldData, '')}
      />
    );
  }

  if (R.equals(uiType, 'selectDropdown')) {
    return (
      <SelectDropdownMuiCell
        data={data}
        field={field}
        width={newWidth}
        settings={settings}
        value={R.or(fieldData, '')}
        callbackData={callbackData}
      />
    );
  }

  if (R.equals(uiType, 'locationObject')) {
    return (
      <LocationComponent value={fieldData} data={data} width={newWidth} field={field} />
    );
  }

  if (R.equals(uiType, 'totalAmount')) {
    return <TotalField data={data} field={field} width={newWidth} value={fieldData} settings={settings} />;
  }

  if (R.equals(uiType, 'state')) {
    return (
      <StateElement value={R.or(fieldData, '')} data={data} width={newWidth} field={field} />
    );
  }

  const dispatchDetailsPages = [
    GC.PAGE_DISPATCH_DETAILS_NEW_LOAD,
    GC.PAGE_DISPATCH_DETAILS_NEW_ORDER,
  ];

  const otherPages = [
    GC.PAGE_FLEET_TRUCK_PROFILE_V2,
    GC.PAGE_FLEET_DRIVER_PROFILE_V2,
    GC.PAGE_FLEET_TRAILER_PROFILE_V2,
  ];

  const expandedContainerPages = [
    ...otherPages,
    ...dispatchDetailsPages,
  ];

  if (R.and(R.includes(uiType, expandedContainerPages), G.hasAmousCurrentUserPermissionsIfIsNotNil(cellPermissions))) {
    const isClo = R.equals(uiType, GC.PAGE_DISPATCH_DETAILS_NEW_ORDER);
    const guid = R.pathOr(G.getGuidFromObject(data), [settings.guidPropName], data);
    const openLoadInSidebar = G.getAmousConfigByNameFromWindow(GC.UI_OPEN_LOAD_IN_SIDEBAR);
    const isOtherPage = R.includes(uiType, otherPages);

    if (R.and(useExpandedContainer, R.or(isOtherPage, openLoadInSidebar))) {
      return (
        <ActionCellWithExpandedContainer
          field={field}
          rowGuid={guid}
          text={fieldData}
          width={newWidth}
          visitPageGuid={guid}
          componentType={uiType}
        />
      );
    }

    const action = () => {
      const route = G.getLoadRouteByConfigAndLoadType(routesMap, guid, isClo);

      if (G.isTrue(openContainerAsNewWindow)) {
        return window.open(
          `${window.location.origin}${route}`,
          'Details',
          'width:200,height:200',
        );
      }

      return G.goToRoute(route);
    };

    return <ActionCell text={fieldData} action={action} width={newWidth} field={field} />;
  }

  if (R.and(G.isTrue(useExpandedContainer), G.hasAmousCurrentUserPermissionsIfIsNotNil(cellPermissions))) {
    const rowGuid = G.getGuidFromObject(data);

    // TODO: check if it is good approach to use rowGuid as default here
    const visitPageGuid = R.pathOr(rowGuid, [settings.guidPropName], data);

    return (
      <ActionCellWithExpandedContainer
        field={field}
        text={fieldData}
        width={newWidth}
        rowGuid={rowGuid}
        componentType={uiType}
        visitPageGuid={visitPageGuid}
        cellPermissions={cellPermissions}
      />
    );
  }

  let valueToDisplay = fieldData;

  if (R.and(R.equals(uiType, 'enumLocale'), G.isNotNilAndNotEmpty(fieldData))) {
    valueToDisplay = G.getEnumLocale(fieldData);
  } else if (R.equals(uiType, GC.FIELD_PERCENT)) {
    valueToDisplay = G.isNilOrEmpty(fieldData) ? '-' : `${fieldData}%`
  } else if (R.and(R.equals(uiType, 'uomLocale'), G.isNotNilAndNotEmpty(fieldData))) {
    valueToDisplay = G.getUomLocale(fieldData);
  } else if (R.equals(uiType, 'collectionOfEnums')) {
    valueToDisplay = R.compose(
      R.join(', '),
      R.map(R.compose(
        R.join(' '),
        R.map(G.toTitleCase),
        R.split('_'),
      )),
      R.uniq,
    )(R.or(fieldData, []));
  } else if (R.equals(uiType, 'html')) {
    let dataToParse = fieldData;

    if (G.isArray(dataToParse)) {
      dataToParse = G.createStringFromArray(fieldData, '/n');
    }

    valueToDisplay = G.getTextFromHtmlString(dataToParse);
  } else if (G.isArray(fieldData)) {
    valueToDisplay = G.createStringFromArray(fieldData, ', ');
  } else if (R.equals(uiType, 'date')) {
    const dataToUse = G.isArray(fieldData) ? fieldData : [fieldData];
    const format = G.ifElse(R.gt(R.length(fieldData), 12), GC.FIELD_DATE_TIME, GC.FIELD_DATE);

    valueToDisplay = R.map((str) => G.checkAndConvertMomentInstanceOrStringToFormattedDate(
      str,
      G.getDateTimeFormatFromConfig(format),
    ), dataToUse);
  } else if (R.equals(uiType, 'number')) {
    valueToDisplay = G.toFixed(fieldData);
  } else if (R.equals(uiType, 'location')) {
    const location = G.getPropFromObject(GC.SYSTEM_OBJECT_LOCATION, data);

    valueToDisplay = G.renderSmallAddressString(location);
  } else if (R.or(
    G.isNilOrEmpty(uiType),
    R.includes(uiType, ['text', 'string']),
  )) {
    if (G.isString(valueToDisplay)) valueToDisplay = R.replace(/\s/g, '\u00a0', valueToDisplay);
  }

  return <TableFieldComponent field={field} width={newWidth} value={valueToDisplay} />;
};

const getFieldData = (fieldName, data, getDataByPath) => {
  if (R.isNil(fieldName)) return null;

  if (R.and(
    G.isTrue(getDataByPath),
    R.gt(R.length(R.split('.', fieldName)), 1)
  )) return P.$get(fieldName, data);

  return G.getPropFromObject(fieldName, data);
};

const getBorderStyles = (i, { freezed, freezedRight }, fields, tableSettings) => {
  if (G.isAllTrue(freezed, R.equals(i, 0), R.equals(0, G.curriedParseInt(tableSettings.checkBoxCellWidth)))) {
    return {
      borderRight: 'none',
      borderRadius: tableSettings.checkBoxCellBorderRadius,
    };
  }

  if (G.isAllTrue(freezedRight, R.not(tableSettings.allowEditBtn), R.equals(i, R.dec(R.length(fields))))) {
    return {
      borderLeft: 'none',
      borderRadius: tableSettings.moreBtnCellBorderRadius,
    };
  }

  return {
    borderLeft: 'none',
    borderRight: 'none',
  };
}

const renderTableFields = (props) => {
  const {
    data,
    fields,
    bgColor,
    rowIndex,
    filterProps,
    tableBgColor,
    resizeByGuid,
    callbackData,
    tableSettings,
    columnSettings,
    tableResizeData,
    lastFreezedIndex,
    useTableUiWithBg,
    withResizableColumns,
    useNewTitleFilterInputs,
  } = props;

  let left = tableSettings.checkBoxCellWidth;

  return fields.map((field, i) => {
    const fieldName = R.prop(GC.FIELD_NAME, field);
    const fieldProps = R.pathOr({}, [fieldName], filterProps);
    const settings = R.path([fieldName], columnSettings);
    const isCustom = G.isNotNilAndNotEmpty(R.path(['customComponent'], settings));
    const uiType = R.path(['type'], settings);
    const getDataByPath = R.path(['getDataByPath'], settings);
    const fieldData = getFieldData(fieldName, data, getDataByPath);
    const width = getWidth({ fieldName, filterProps, columnSettings, useNewTitleFilterInputs });

    const resizable = isColumnResizable({
      fieldName,
      fieldProps,
      columnSettings,
      withResizableColumns,
    });

    let storedWidth;
    if (resizable) {
      storedWidth = R.prop(fieldName, resizeByGuid);
    }

    const newWidth = R.or(storedWidth, width);

    const cellLeft = left;

    left = R.add(left, R.or(newWidth, 0));

    const useExpandedContainer = G.getPropFromObject('useExpandedContainer', settings);
    const openContainerAsNewWindow = G.getPropFromObject('openContainerAsNewWindow', settings);

    if (R.or(field.freezed, field.freezedRight)) {
      const positionOption = G.ifElse(
        field.freezed,
        { left: R.or(field.left, cellLeft) },
        { right: R.or(field.right, R.or(tableSettings.moreBtnCellWidth, '0px')) },
      );

      return (
        <StickedWrapper
          {...positionOption}
          zIndex={1}
          width={newWidth}
          bgColor={tableBgColor}
          position={field.position}
          key={`sticked-field-table-${i}`}
          height={tableSettings.tableRowHeight}
        >
          <CellWithBackground
            bgColor={bgColor}
            highlighted={data.highlighted}
            className='cell-with-background'
            activeRowColor={tableSettings.activeRowColor}
            mainColors={G.isTrue(tableSettings.useMainColors)}
            active={R.and(G.isTrue(data.selected), G.isTrue(tableSettings.coloredActiveRow))}
            {...G.spreadUiStyles(getBorderStyles(i, field, fields, tableSettings))}
          >
            <WithBoxShadow boxShadow={G.ifElse(R.equals(lastFreezedIndex, i), false, 'unset')}>
              <TableField
                data={data}
                field={field}
                width={width}
                uiType={uiType}
                rowIndex={rowIndex}
                settings={settings}
                isCustom={isCustom}
                resizable={resizable}
                fieldData={fieldData}
                key={`field-table-${i}`}
                resizeByGuid={resizeByGuid}
                callbackData={callbackData}
                tableResizeData={tableResizeData}
                useExpandedContainer={useExpandedContainer}
                cellPermissions={G.getPropFromObject('cellPermissions', settings)}
              />
            </WithBoxShadow>
          </CellWithBackground>
        </StickedWrapper>
      );
    }

    return (
      <TableField
        data={data}
        field={field}
        width={width}
        uiType={uiType}
        rowIndex={rowIndex}
        settings={settings}
        isCustom={isCustom}
        fieldData={fieldData}
        resizable={resizable}
        key={`field-table-${i}`}
        resizeByGuid={resizeByGuid}
        callbackData={callbackData}
        tableResizeData={tableResizeData}
        useExpandedContainer={useExpandedContainer}
        openContainerAsNewWindow={openContainerAsNewWindow}
        cellPermissions={G.getPropFromObject('cellPermissions', settings)}
      />
    );
  });
};

const getRowBorderStyles = (freezedProp, fields, withStickedCell) => {
  if (R.or(G.isTrue(withStickedCell), R.any(R.prop(freezedProp), fields))) return 'none';
}

const TableRowComponent = withBooleanStatus(false)((props) => {
  const {
    item,
    report,
    zIndex,
    toggle,
    rowIndex,
    rowStyles,
    iconColor,
    filterProps,
    callbackData,
    resizeByGuid,
    tableSettings,
    onOptionClick,
    rowBgFunction,
    actionButtons,
    columnSettings,
    mainLightColor,
    tableResizeData,
    setTableRowHover,
    lastFreezedIndex,
    useTableUiWithBg,
    unsetTableRowHover,
    withResizableColumns,
    handleShowListIssues,
    useNewTitleFilterInputs,
    renderLeftStickedComponent,
    renderRightStickedComponent,
  } = props;

  if (tableSettings.withOnlyExpandedDetails) return null;

  const textDecoration = G.ifElse(
    G.isTrue(item.cancelled),
    'line-through',
    'none',
  );

  const maxIssueWarningLevelValue = G.getPropFromObject(GC.FIELD_MAX_ISSUE_WARNING_LEVEL, item);
  const issues = G.getPropFromObject(GC.FIELD_ISSUES, item);
  const hasIssues = G.isNotNilAndNotEmpty(issues);

  let bgColor = getRowBgColor(issues, maxIssueWarningLevelValue);

  if (G.isFunction(rowBgFunction)) {
    bgColor = rowBgFunction(item[GC.FIELD_GUID], item);
  }

  const reportFields = G.getPropFromObject('fields', report);

  const allowEditBtn = G.isTrue(tableSettings.allowEditBtn);
  const shouldShowStickedCellWithIcons = R.gt(G.curriedParseInt(tableSettings.checkBoxCellWidth), 0);

  const tableRowStyles = R.mergeRight({
    my: '3px',
    borderRadius: 10,
    borderRight: getRowBorderStyles('freezedRight', reportFields, allowEditBtn),
    borderLeft: getRowBorderStyles('freezed', reportFields, shouldShowStickedCellWithIcons),
  }, rowStyles);

  const tableBgColor =  G.getThemeByCond(useTableUiWithBg, 'colors.#F8FAFD', 'colors.white');

  return (
    <TableRow
      {...tableRowStyles}
      bgColor={bgColor}
      highlighted={item.highlighted}
      key={`table-row-${item.guid}`}
      textDecoration={textDecoration}
      fontSize={tableSettings.cellFontSize}
      height={tableSettings.tableRowHeight}
      pl={tableSettings.absoluteColumnWidth}
      activeRowColor={tableSettings.activeRowColor}
      mainColors={G.isTrue(tableSettings.useMainColors)}
      onMouseOver={() => G.callFunctionWithArgs(setTableRowHover, item)}
      onMouseLeave={() => G.callFunctionWithArgs(unsetTableRowHover, item)}
      active={R.and(G.isTrue(item.selected), G.isTrue(tableSettings.coloredActiveRow))}
    >
      {
        shouldShowStickedCellWithIcons &&
        <StickedCellWithIcons
          item={item}
          issues={issues}
          toggle={toggle}
          bgColor={bgColor}
          rowIndex={rowIndex}
          iconColor={iconColor}
          hasIssues={hasIssues}
          tableBgColor={tableBgColor}
          onOptionClick={onOptionClick}
          tableSettings={tableSettings}
          actionButtons={actionButtons}
          mainLightColor={mainLightColor}
          lastFreezedIndex={lastFreezedIndex}
          handleShowListIssues={handleShowListIssues}
          renderLeftStickedComponent={renderLeftStickedComponent}
        />
      }
      {
        renderTableFields({
          bgColor,
          rowIndex,
          data: item,
          filterProps,
          tableBgColor,
          callbackData,
          resizeByGuid,
          tableSettings,
          columnSettings,
          tableResizeData,
          lastFreezedIndex,
          useTableUiWithBg,
          withResizableColumns,
          fields: reportFields,
          useNewTitleFilterInputs,
        })
      }
      {
        allowEditBtn &&
        <StickedWrapper
          ml='auto'
          right='0px'
          bgColor={tableBgColor}
          height={tableSettings.tableRowHeight}
          width={tableSettings.moreBtnCellWidth}
        >
          <CellWithBackground
            borderLeft='none'
            bgColor={bgColor}
            className='cell-with-background'
            activeRowColor={tableSettings.activeRowColor}
            mainColors={G.isTrue(tableSettings.useMainColors)}
            borderRadius={tableSettings.moreBtnCellBorderRadius}
            active={R.and(G.isTrue(item.selected), G.isTrue(tableSettings.coloredActiveRow))}
          >
            <FlexContainer p='8px' height='100%' justify='center' direction='column'>
              <RightStickedComponent
                item={item}
                rowIndex={rowIndex}
                renderRightStickedComponent={
                  (item) => (
                    G.isFunction(renderRightStickedComponent)
                    && renderRightStickedComponent(item, props.setBooleanStatus)
                  )
                }
              />
            </FlexContainer>
          </CellWithBackground>
        </StickedWrapper>
      }
    </TableRow>
  );
});

const renderList = (props) => {
  const count = props.itemList.length;
  const iconColor = G.getTheme('icons.iconColor');
  const mainLightColor = G.getTheme('colors.light.mainLight');

  return props.itemList.map((item, i) => (
    [
      <TableRowComponent
        {...props}
        item={item}
        rowIndex={i}
        iconColor={iconColor}
        mainLightColor={mainLightColor}
        key={`table-list-row-${i}${item.guid}`}
        handleShowListIssues={props.handleShowListIssues}
      />,
      <AdditionalRowComponent
        item={item}
        AdditionalRow={props.AdditionalRow}
        key={`table-additional-row-${i}${item.guid}`}
      />,
      <ExpandedDetails
        item={item}
        callbackData={props.callbackData}
        key={`expanded-${i}${item.guid}`}
        tableSettings={props.tableSettings}
      />,
    ]
  ));
};

const TableDimmerWrapper = styled(Box)`
  animation: ${pulseOpacity} 2s infinite linear;
`;

export const TableDimmer = (props) => (
  <TableDimmerWrapper minWidth='100vw'>
    {
      props.withHeader &&
      <Box
        bg='#AFAFAF'
        width='100vw'
        borderRadius='10px'
        height={props.tableSettings.titleRowHeight}
      />
    }
    {
      G.getComponentCountTimes(
        props.count,
        (_, i) => (
          <Box
            key={i}
            mt='2px'
            bg='#d8d8d8'
            width='100vw'
            borderRadius='5px'
            height={R.subtract(props.tableSettings.tableRowHeight, 2)}
          />
        )
        ,
      )
    }
  </TableDimmerWrapper>
);

export const TableBody = (props) => {
  const {
    data,
    loading,
    withWaypoint,
    resizeByGuid,
    resizeObserver,
    handleUpdateTableResize,
  } = props;

  const dataWithResize = R.mergeRight(data, {
    resizeByGuid,
    resizeObserver,
    handleUpdateTableResize,
  });

  if (R.and(G.isNilOrEmpty(data.itemList), R.not(loading))) {
    return (
      <>
        <TableInner minWidth={data.tableSettings.minWidth}>
          {
            data.tableSettings.withTitleRow &&
            <TitleRowComponent {...dataWithResize} />
          }
        </TableInner>
        {
          R.not(data.tableSettings.withoutEmptyList) &&
          <EmptyTableList
            p={data.tableSettings.emptyListPadding}
            textAlign={data.tableSettings.emptyListTextAlign}
          >
            {G.getWindowLocale('titles:empty-list', 'Empty List')}
          </EmptyTableList>
        }
      </>
    );
  }

  return (
    <TableInner minWidth={data.tableSettings.minWidth}>
      {
        data.tableSettings.withTitleRow &&
        <TitleRowComponent {...dataWithResize} />
      }
      {G.isNotNil(R.path(['subTitle'], data)) && data.subTitle}
      {renderList(dataWithResize)}
      {
        R.and(G.isTrue(withWaypoint), R.not(loading)) &&
        <Waypoint onEnter={data.handleLoadMoreEntities} />
      }
      {
        loading &&
        <LocalLoader minWidth={340} localLoaderOpen={true} />
      }
    </TableInner>
  );
};
